import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Modal,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
  outline: "none",
  overflow: "hidden",
};

const AddToJobBtn = ({
  openAddtoJob,
  setOpenAddtoJob,
  addToJobCandidateids,
  setSelectedRows,
  setAddToJobCandidateids,
  setRenderKey,
  onCandidateAdded,
}) => {
  const [shimmer, setShimmer] = useState(false);
  const [failCandidateData, setFailCandidateData] = useState([]);
  const [allJobsData, setallJobsData] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: null,
    severity: "success",
  });

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`all-jobs`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      setallJobsData(response.data.message);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, []);

  const handleCloseOpenAddtoJob = () => {
    setOpenAddtoJob(false);
    setSelectedJob(null);
  };

  const handleAddCandidates = async () => {
    setShimmer(true);
    try {
      const response = await axios.post(
        `bulk-add/job`,
        {
          candidate_ids: addToJobCandidateids,
          job_id: selectedJob ? selectedJob.joborder_id : "",
          quesAnsArray: [],
          isShortlisted: 0,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );

      if (response.data.status === 200) {
        setFailCandidateData(response.data.message.details);
        setShimmer(false);
        setSelectedJob(null);
        setRenderKey((prevKey) => prevKey + 1);
        setSnackbar({
          open: true,
          message: response?.data?.message["message"],
          severity: "success",
        });
        setSelectedRows([]);
        setAddToJobCandidateids([]);
        if (onCandidateAdded) onCandidateAdded();
        setTimeout(() => {
          setOpenAddtoJob(false);
        }, 6000);
      } else {
        setShimmer(false);
        setSnackbar({
          open: true,
          message: response.data.error,
          severity: "error",
        });
      }
    } catch (error) {
      setShimmer(false);
      setSnackbar({
        open: true,
        message: error.response.data.error,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Modal
        open={openAddtoJob}
        onClose={handleCloseOpenAddtoJob}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalLoadingContainerSx(shimmer, style)}>
          {/* ── Header: 8px 20px padding + border bottom ── */}
          <Box
            sx={{
              px: "20px",
              py: "12px",
              borderBottom: "1px solid #E8EDF2",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              id="modal-modal-title"
              sx={{
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 600,
                color: "#0F172A",
                lineHeight: "normal",
              }}
            >
              Add to Job
            </Typography>
            <IconButton
              type="button"
              size="small"
              onClick={handleCloseOpenAddtoJob}
              aria-label="Close"
              sx={{
                color: "#64748B",
                "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.06)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* ── Body ── */}
          <Box sx={{ px: "20px", pt: "20px", pb: "8px" }}>
            {/* Select Job label */}
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Select Job
            </Typography>

            {/* Rounded corners dropdown — image 2 style */}
            <Autocomplete
              id="controllable-states-demo"
              options={allJobsData}
              getOptionLabel={(option) =>
                `${option.title}, ${option.joborder_id}`
              }
              value={selectedJob}
              onChange={(event, newValue) => setSelectedJob(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Job"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "14px",
                      color: "#374151",
                      backgroundColor: "#fff",
                      height: "42px",
                      "& fieldset": { borderColor: "#D1D5DB" },
                      "&:hover fieldset": { borderColor: "#3F95EA" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3F95EA",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#9CA3AF",
                      opacity: 1,
                    },
                  }}
                />
              )}
              ListboxProps={{
                sx: {
                  borderRadius: "10px",
                  mt: "4px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  "& .MuiAutocomplete-option": {
                    fontSize: "14px",
                    "&:hover": { backgroundColor: "#EBF5FF" },
                    "&.Mui-focused": { backgroundColor: "#EBF5FF" },
                  },
                },
              }}
            />

            {/* Fail candidate section — same as original logic */}
            <Box sx={{ mt: 2 }}>
              {shimmer ? (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Skeleton variant="text" width="100%" />
                  <Box display="flex" gap={1.5} flexWrap="wrap">
                    {[1, 2].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rectangular"
                        width={220}
                        height={60}
                        sx={{ borderRadius: "8px" }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                failCandidateData.length > 0 && (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mb: 1.5 }}
                    >
                      {failCandidateData.length > 1 ? "These" : "This"}{" "}
                      {failCandidateData.length}{" "}
                      {failCandidateData.length > 1
                        ? "candidates"
                        : "candidate"}{" "}
                      {failCandidateData.length > 1 ? "are" : "is"} already
                      available in this job. The remaining candidates have been
                      added successfully.
                    </Typography>
                    <Box display="flex" gap={1.5} flexWrap="wrap">
                      {failCandidateData.map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FECACA",
                            p: 1.5,
                            borderRadius: "8px",
                            width: 220,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#111827",
                            }}
                          >
                            {item.first_name} {item.last_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#6B7280",
                              mt: 0.3,
                            }}
                          >
                            {item.email1}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </Box>

          {/* ── Footer: Cancel + Add ── */}
          <Box
            sx={{
              px: "20px",
              py: "14px",
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              alignItems: "center",
            }}
          >
            {/* Cancel — image 3 style: outlined, rounded, light border */}
            <Button
              className="cancel-btn"
              variant="outlined"
              size="small"
              onClick={handleCloseOpenAddtoJob}
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#6B7280",
                borderColor: "#D1D5DB",
                borderRadius: "8px",
                px: 2.5,
                py: 0.8,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#9CA3AF",
                  backgroundColor: "#F9FAFB",
                  boxShadow: "none",
                },
              }}
            >
              Cancel
            </Button>

            {/* Add — #3F95EA enabled, #006CB7 hover, grey when disabled */}
            <Button
              variant="contained"
              size="small"
              onClick={handleAddCandidates}
              disabled={!selectedJob}
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                borderRadius: "8px",
                px: 2.5,
                py: 0.8,
                backgroundColor: "#3F95EA",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#006CB7",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#E5E7EB",
                  color: "#9CA3AF",
                },
              }}
            >
              Add
            </Button>
          </Box>
          <ModalLoadingOverlay open={shimmer} />
        </Box>
      </Modal>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddToJobBtn;
